import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { Mail, School, ChevronRight } from 'lucide-react';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
type Role = 'learner' | 'parent' | 'teacher';
interface RegisterDetailsStepProps {
  role: Role;
  grade: string;
  onGradeChange: (grade: string) => void;
  email: string;
  onEmailChange: (email: string) => void;
  schoolName: string;
  onSchoolNameChange: (name: string) => void;
  schoolSearch: string;
  onSchoolSearchChange: (search: string) => void;
  schoolInputMode: 'search' | 'manual';
  onSchoolInputModeChange: (mode: 'search' | 'manual') => void;
  schoolSelected: boolean;
  onSchoolSelected: (selected: boolean) => void;
  existingSchools: string[];
  onNext: () => void;
  slideVariants: Record<string, unknown>;
}
export function RegisterDetailsStep({
  role,
  grade,
  onGradeChange,
  email,
  onEmailChange,
  schoolName,
  onSchoolNameChange,
  schoolSearch,
  onSchoolSearchChange,
  schoolInputMode,
  onSchoolInputModeChange,
  schoolSelected,
  onSchoolSelected,
  existingSchools,
  onNext,
  slideVariants
}: RegisterDetailsStepProps) {
  const itemVariants = {
    enter: {
      opacity: 0,
      y: 20
    },
    center: {
      opacity: 1,
      y: 0
    },
    exit: {
      opacity: 0,
      y: -20
    }
  };
  return <motion.div key="step-details" custom={1} variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{
    duration: 0.3,
    staggerChildren: 0.1
  }} className="space-y-5" data-id="element-528">
      
      <motion.h2 variants={itemVariants} className="text-xl font-black text-slate-900 text-center" data-id="element-529">
        
        {role === 'learner' ? 'In welke groep zit je?' : role === 'teacher' ? 'Over jouw school' : 'Nog een paar details'}
      </motion.h2>

      {/* Learner: Grade selection */}
      {role === 'learner' && <motion.div variants={itemVariants} className="grid grid-cols-3 gap-3" data-id="element-530">
          {['6', '7', '8'].map(g => {
        const isActive = grade === g;
        const emoji = g === '6' ? '🌟' : g === '7' ? '🚀' : '🏆';
        return <motion.button key={g} type="button" onClick={() => {
          onGradeChange(g);
          setTimeout(onNext, 350);
        }} className={`py-6 rounded-2xl border-2 text-center transition-colors relative overflow-hidden ${isActive ? 'border-orange-400 bg-orange-50 text-orange-700 shadow-sm' : 'border-slate-200 bg-white text-slate-600 hover:border-orange-200 hover:bg-orange-50/50'}`} whileTap={{
          scale: 0.95
        }} animate={isActive ? {
          scale: [1, 1.1, 1.05],
          y: -4
        } : {
          scale: 1,
          y: 0
        }} transition={{
          type: 'spring',
          stiffness: 400,
          damping: 15
        }} data-id="element-531">
              
                <div className="absolute -right-2 -bottom-2 text-4xl opacity-10 grayscale filter" data-id="element-532">
                  {emoji}
                </div>
                <div className="relative z-10" data-id="element-533">
                  <div className="text-2xl mb-1" data-id="element-534">{emoji}</div>
                  <div className="text-xs font-bold opacity-60 mb-1" data-id="element-535">Groep</div>
                  <div className="text-3xl font-black" data-id="element-536">{g}</div>
                </div>
              </motion.button>;
      })}
        </motion.div>}

      {/* Parent: Email */}
      {role === 'parent' && <motion.div variants={itemVariants} data-id="element-537">
          <label className="block text-sm font-bold text-slate-600 mb-1.5" data-id="element-538">
            E-mailadres
          </label>
          <div className="relative" data-id="element-539">
            <Input type="email" required autoFocus value={email} onChange={e => onEmailChange(e.target.value)} icon={<Mail className="w-5 h-5" data-id="element-541" />} className="bg-slate-50 border-2 border-slate-200 rounded-2xl focus:bg-white focus:border-orange-400 text-lg font-medium py-4" placeholder="naam@voorbeeld.nl" data-id="element-540" />
          
          </div>
        </motion.div>}

      {/* Teacher: Email + School */}
      {role === 'teacher' && <motion.div variants={itemVariants} className="space-y-4" data-id="element-542">
          <div data-id="element-543">
            <label className="block text-sm font-bold text-slate-600 mb-1.5" data-id="element-544">
              E-mailadres
            </label>
            <div className="relative" data-id="element-545">
              <Input type="email" required autoFocus value={email} onChange={e => onEmailChange(e.target.value)} icon={<Mail className="w-5 h-5" data-id="element-547" />} className="bg-slate-50 border-2 border-slate-200 rounded-2xl focus:bg-white focus:border-orange-400 text-lg font-medium py-4" placeholder="naam@school.nl" data-id="element-546" />
            
            </div>
          </div>
          <div data-id="element-548">
            <label className="block text-sm font-bold text-slate-600 mb-1.5" data-id="element-549">
              School
            </label>

            {/* Selected school display */}
            {schoolName && schoolSelected ? <div className="flex items-center gap-3 p-4 bg-orange-50 border-2 border-orange-200 rounded-2xl" data-id="element-550">
                <div className="w-10 h-10 bg-orange-100 rounded-xl flex items-center justify-center" data-id="element-551">
                  <School className="w-5 h-5 text-orange-600" data-id="element-552" />
                </div>
                <div className="flex-1" data-id="element-553">
                  <p className="font-bold text-slate-900" data-id="element-554">{schoolName}</p>
                  <p className="text-xs text-slate-500" data-id="element-555">Geselecteerd</p>
                </div>
                <button type="button" onClick={() => {
            onSchoolNameChange('');
            onSchoolSearchChange('');
            onSchoolSelected(false);
          }} className="text-sm text-orange-600 hover:text-orange-700 font-bold" data-id="element-556">
              
                  Wijzig
                </button>
              </div> : schoolInputMode === 'search' && existingSchools.length > 0 ? <div className="space-y-3" data-id="element-557">
                <div className="relative" data-id="element-558">
                  <Input type="text" value={schoolSearch} onChange={e => onSchoolSearchChange(e.target.value)} icon={<School className="w-5 h-5" data-id="element-560" />} className="bg-slate-50 border-2 border-slate-200 rounded-2xl focus:bg-white focus:border-orange-400 text-lg font-medium py-4" placeholder="Zoek je school..." autoComplete="off" data-id="element-559" />
              
                </div>

                {/* Matching schools list */}
                <div className="bg-white border-2 border-slate-200 rounded-2xl overflow-hidden max-h-48 overflow-y-auto" data-id="element-561">
                  {existingSchools.filter(s => !schoolSearch.trim() || s.toLowerCase().includes(schoolSearch.toLowerCase())).map(school => <button key={school} type="button" onClick={() => {
              onSchoolNameChange(school);
              onSchoolSearchChange('');
              onSchoolSelected(true);
            }} className="w-full text-left px-4 py-3.5 hover:bg-orange-50 text-slate-700 font-medium transition-colors border-b border-slate-100 last:border-0 flex items-center gap-3" data-id="element-562">
                
                        <School className="w-4 h-4 text-slate-400 flex-shrink-0" data-id="element-563" />
                        {school}
                      </button>)}
                  {schoolSearch.trim() && existingSchools.filter(s => s.toLowerCase().includes(schoolSearch.toLowerCase())).length === 0 && <div className="px-4 py-4 text-center text-slate-500 text-sm" data-id="element-564">
                        Geen scholen gevonden voor "{schoolSearch}"
                      </div>}
                </div>

                {/* Add new school button */}
                <button type="button" onClick={() => {
            onSchoolInputModeChange('manual');
            onSchoolNameChange(schoolSearch);
          }} className="w-full text-center py-3 text-sm font-bold text-orange-600 hover:text-orange-700 hover:bg-orange-50 rounded-xl transition-colors" data-id="element-565">
              
                  School niet gevonden? Voeg handmatig toe →
                </button>
              </div> : <div className="space-y-2" data-id="element-566">
                <div className="relative" data-id="element-567">
                  <Input type="text" required value={schoolName} onChange={e => onSchoolNameChange(e.target.value)} icon={<School className="w-5 h-5" data-id="element-569" />} className="bg-slate-50 border-2 border-slate-200 rounded-2xl focus:bg-white focus:border-orange-400 text-lg font-medium py-4" placeholder="bijv. Basisschool De Regenboog" autoComplete="off" autoFocus data-id="element-568" />
              
                </div>
                {existingSchools.length > 0 && <button type="button" onClick={() => {
            onSchoolInputModeChange('search');
            onSchoolNameChange('');
            onSchoolSearchChange('');
            onSchoolSelected(false);
          }} className="text-sm font-bold text-slate-500 hover:text-orange-600 transition-colors" data-id="element-570">
              
                    ← Terug naar zoeken
                  </button>}
              </div>}
          </div>
        </motion.div>}

      {/* Next button for non-learner (learner auto-advances on grade pick) */}
      {role !== 'learner' && <motion.div variants={itemVariants} data-id="element-571">
          <Button type="submit" variant="primary" size="lg" className="w-full" disabled={role === 'teacher' ? !email.trim() || !schoolName.trim() : !email.trim()} data-id="element-572">
          
            Verder <ChevronRight className="w-5 h-5 ml-1" data-id="element-573" />
          </Button>
        </motion.div>}
    </motion.div>;
}